import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import ratnaImage from '../../../../images/discovery/ratna.svg';
import RatnaBullet from '../../../Icons/icons/RatnaBullet';
import SupportQuestion from '../../../Icons/icons/SupportQuestion';
import ButtonComponent from '../../ButtonComponent/ButtonComponent';

import '../_modalComponent.scss';

import { getRatnaTriggerAlerts } from '@/services/ratnaTrigger.service';
import { RATNA_WARNING_TYPES } from '@/utils/constants/ratna';
import dayjs from 'dayjs';
import { NotificationManager } from 'react-notifications';

import RatnaProgress from '@/components/Dashboard/components/RatnaProgress';

import RatnaWarning from './RatnaWarning';

interface RatnaDescriptionProps {
  setLeftSection: (section: string) => void;
}

interface UserDetails {
  subscriptionStatus?: string;
}

interface OptionItemProps {
  title: string;
  subTitle: string;
  id?: number;
}

const ratnaDescriptionV1Users: OptionItemProps[] = [
  {
    title: '1 Ratna = 1 Creator saved to your list',
    subTitle:
      'Save creators to your lists with detailed metrics and contact info, including email. You can also use it to reveal +100 more creators.',
    id: 1,
  },
  {
    title: 'Why is it priced like this?',
    subTitle:
      'We get this data from social media APIs, it costs us more when you save more. There are no limits on how many you can manage after saving.',
    id: 2,
  },
  {
    title: 'Take back your time with Ratna',
    subTitle: 'No more spending hours every week finding creators manually. We allow you to search creators in bulk.',
    id: 3,
  },
];
const ratnaDescriptionV2Users: OptionItemProps[] = [
  {
    title: '1 Ratna = 1 Creator saved to your list',
    subTitle: 'Save creators to your lists with detailed metrics and contact info, including  email.',
    id: 1,
  },
  {
    title: 'Do Ratnas carry over?',
    subTitle:
      'Ratnas are use it or lose it. Your credits reset back to what your plan allows for every month. This is so you actually reach out to influencers each month!',
    id: 2,
  },
  {
    title: 'What if I want more?',
    subTitle: 'You can request for more Ratnas by upgrading your plan or buying a booster pack.',
    id: 3,
  },
];

const RatnaDescription: React.FC<RatnaDescriptionProps> = ({ setLeftSection }) => {
  const { userDetails } = useSelector((state: any) => state.user);
  const [ratnaBannerType, setRatnaBannerType] = useState<string | null>(null);

  const isV2User = userDetails?.accountVersion === 'v2' ? true : false;

  const isTrialUser = (userDetails as UserDetails)?.subscriptionStatus === 'trial';

  const ratnaDescription = isV2User ? ratnaDescriptionV2Users : ratnaDescriptionV1Users;

  const totalLimit = userDetails?.plan?.ratnaCount || 300;
  const totalUsed = totalLimit - (userDetails?.balance || 0);

  const fetchRatnaAlert = async () => {
    const response = await getRatnaTriggerAlerts();
    if (response.status) {
      if (response?.data?.show25TagInRatnaModal) {
        setRatnaBannerType(RATNA_WARNING_TYPES.TWENTY_FIVE_PERCENT_CONSUMPTION);
      } else if (response?.data?.show50TagInRatnaModal) {
        setRatnaBannerType(RATNA_WARNING_TYPES.FIFTY_PERCENT_CONSUMPTION);
      } else if (totalUsed >= totalLimit && totalUsed > 0 && totalLimit > 0) {
        setRatnaBannerType(RATNA_WARNING_TYPES.ONE_HUNDRED_PERCENT_CONSUMPTION);
      }
    } else {
      NotificationManager.error('Failed to fetch ratna alert');
    }
  };

  useEffect(() => {
    fetchRatnaAlert();
  }, []);

  return (
    <>
      <div className="flex flex-between items-center gap-2 mb-6">
        <h2 className="text-xl text-default m-0 font-medium">Your Ratna Usage</h2>

        <div className="border-default h-8 text-sm flex items-center gap-2 px-4 rounded-4xl">
          Current Balance{' '}
          <span className="flex items-center gap-1">
            <img src={ratnaImage} width={20} height={20} alt="ratna" />
            <span className="font-medium">{userDetails?.balance}</span>
          </span>
        </div>
      </div>
      <div>
        <div className="flex flex-between items-center gap-2 mb-1">
          <label htmlFor="ratna-progress" className="text-sm text-subdued">
            Monthly Usage:
          </label>
          <p className="text-xs text-subdued flex items-center gap-1">
            Renews on
            <span className="text-success font-medium">
              {dayjs.unix(userDetails.nextFreeCreditRatnaAt).format('DD MMM YYYY')}
            </span>
          </p>
        </div>
        <RatnaProgress value={totalUsed} limit={totalLimit} />
        <div className="my-4">
          <RatnaWarning type={ratnaBannerType} />
        </div>
      </div>

      <div className="ratna-left-description-container">
        <div className="ratna-description-container">
          <OptionItem
            title={`You get ${userDetails?.plan?.ratnaCount || 300}  Ratna each month`}
            subTitle={`On your current plan, you get ${userDetails?.plan?.ratnaCount || 300} each month. You can get a tailor-made plan to  get more. ${
              isTrialUser ? 'You are currently on the free trial, so you will get 50 to test.' : ''
            }`}
          />
          {ratnaDescription.map(({ id, title, subTitle }) => (
            <OptionItem title={title} subTitle={subTitle} key={id} />
          ))}
        </div>
        <div className="modal-footer-button-container small-button-container ratna-description-buttons-container">
          <ButtonComponent
            variant="secondary"
            className="learn-more-button"
            onClick={() => {
              window.open('https://help.getsaral.com/en/articles/200-what-the-ratna-gem-does-in-the-app');
            }}>
            <SupportQuestion color="currentColor" />
            Learn More
          </ButtonComponent>
          <ButtonComponent
            variant="secondary-dark"
            className="one-time-ratna-button"
            onClick={() => setLeftSection('buy-ratna')}>
            <img width="20px" height="20px" src={ratnaImage} alt="celebration" style={{ marginBottom: '-3px' }} />
            One-time Ratna Boost
          </ButtonComponent>
        </div>
      </div>
    </>
  );
};

const OptionItem: React.FC<OptionItemProps> = ({ title, subTitle }) => {
  return (
    <div className="ratna-description-option">
      <div className="bullet-container">
        <RatnaBullet />
      </div>
      <div className="description-text-container">
        <div className="description-text-title">{title}</div>
        <div className="description-text-sub-title">{subTitle}</div>
      </div>
    </div>
  );
};

export default RatnaDescription;
